import React from 'react';
import { Alert, List, Typography } from 'antd';
import type { QuotationRevisionInsight } from '@/utils/quotationRevisionIntelligence';
import type { QuotationRevisionCompareResult } from '@/components/crm/QuotationRevisionComparePanel';
import type { Quotation } from '@/services/quotation.service';
import dayjs from 'dayjs';

const { Text } = Typography;

type Props = {
  insights: QuotationRevisionInsight[];
  comparison?: QuotationRevisionCompareResult;
  quotation: Quotation;
  universalApprovalPending?: boolean;
  onInsightAction?: (key: NonNullable<QuotationRevisionInsight['actionKey']>) => void;
};

export const QuotationAttentionPanel: React.FC<Props> = ({
  insights,
  comparison,
  quotation,
  universalApprovalPending,
  onInsightAction,
}) => {
  const items: { severity: 'info' | 'warning' | 'success' | 'error'; message: string; actionKey?: QuotationRevisionInsight['actionKey'] }[] =
    [];

  if (comparison?.commercial_impact?.margin_pct_delta_pp != null &&
      comparison.commercial_impact.margin_pct_delta_pp < -0.005) {
    items.push({
      severity: 'warning',
      message: `Margin reduced by ${Math.abs(comparison.commercial_impact.margin_pct_delta_pp).toFixed(2)}pp vs prior revision.`,
    });
  }

  if (universalApprovalPending) {
    items.push({ severity: 'info', message: 'Approval pending for this revision.' });
  }

  if (quotation.valid_until) {
    const days = dayjs(quotation.valid_until).diff(dayjs(), 'day');
    if (days < 0 && quotation.is_current_submitted) {
      items.push({ severity: 'error', message: 'Quotation validity has expired.' });
    } else if (days >= 0 && days <= 7 && quotation.is_current_submitted) {
      items.push({ severity: 'warning', message: `Validity expires in ${days} day${days === 1 ? '' : 's'}.` });
    }
  }

  if (!quotation.estimation_id && !quotation.boq_id && !quotation.trading_rental_estimation_id) {
    items.push({ severity: 'info', message: 'No linked Estimate — commercial-only quotation.' });
  }

  for (const insight of insights) {
    if (items.some((i) => i.message === insight.message)) continue;
    items.push(insight);
  }

  if (items.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Needs Attention
      </Text>
      <List
        size="small"
        style={{ marginTop: 8 }}
        dataSource={items.slice(0, 6)}
        renderItem={(item) => (
          <List.Item
            style={{ padding: '4px 0', border: 'none' }}
            actions={
              item.actionKey && onInsightAction
                ? [
                    <a key="act" onClick={() => onInsightAction(item.actionKey!)}>
                      Action
                    </a>,
                  ]
                : undefined
            }
          >
            <Alert type={item.severity} showIcon message={item.message} style={{ width: '100%' }} />
          </List.Item>
        )}
      />
    </div>
  );
};

export default QuotationAttentionPanel;
